// ABOUTME: Context for managing upgrade modal state when usage limits are exceeded.
// ABOUTME: Listens for 'limit-exceeded' events and provides state/actions for UpgradeModal.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

export interface LimitInfo {
  resource: 'messages' | 'active_investigations' | 'data_sources' | 'reports';
  used: number;
  limit: number;
  resetsAt?: string;
}

interface UpgradeContextType {
  showUpgradeModal: boolean;
  limitInfo: LimitInfo | null;
  showLimitExceeded: (info: LimitInfo) => void;
  hideModal: () => void;
}

const UpgradeContext = createContext<UpgradeContextType | undefined>(undefined);

interface UpgradeProviderProps {
  children: ReactNode;
}

export function UpgradeProvider({ children }: UpgradeProviderProps) {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<LimitInfo | null>(null);

  const showLimitExceeded = useCallback((info: LimitInfo) => {
    setLimitInfo(info);
    setShowUpgradeModal(true);
  }, []);

  const hideModal = useCallback(() => {
    setShowUpgradeModal(false);
    setLimitInfo(null);
  }, []);

  // Listen for limit-exceeded events from the API client
  useEffect(() => {
    const handleLimitExceeded = (event: CustomEvent<LimitInfo>) => {
      showLimitExceeded(event.detail);
    };

    window.addEventListener('limit-exceeded', handleLimitExceeded as EventListener);

    return () => {
      window.removeEventListener('limit-exceeded', handleLimitExceeded as EventListener);
    };
  }, [showLimitExceeded]);

  const value: UpgradeContextType = {
    showUpgradeModal,
    limitInfo,
    showLimitExceeded,
    hideModal,
  };

  return (
    <UpgradeContext.Provider value={value}>{children}</UpgradeContext.Provider>
  );
}

export function useUpgrade() {
  const context = useContext(UpgradeContext);
  if (!context) {
    throw new Error('useUpgrade must be used within an UpgradeProvider');
  }
  return context;
}
